import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningIcon from '@mui/icons-material/Warning';
import { CHANGELOG, ChangelogEntry } from '../../data/changelog';

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Get the icon for a change category
 */
const getCategoryIcon = (category: ChangelogEntry['changes'][0]['category']) => {
  switch (category) {
    case 'feature':
      return <AutoAwesomeIcon sx={{ fontSize: 16 }} className="text-blue-500 dark:text-blue-400" />;
    case 'bugfix':
      return <CheckCircleIcon sx={{ fontSize: 16 }} className="text-green-500 dark:text-green-400" />;
    case 'improvement':
      return <InfoIcon sx={{ fontSize: 16 }} className="text-purple-500 dark:text-purple-400" />;
    case 'breaking':
      return <WarningIcon sx={{ fontSize: 16 }} className="text-red-500 dark:text-red-400" />;
  }
};

/**
 * Get the label for a change category
 */
const getCategoryLabel = (category: ChangelogEntry['changes'][0]['category']) => {
  switch (category) {
    case 'feature':
      return 'New Feature';
    case 'bugfix':
      return 'Bug Fix';
    case 'improvement':
      return 'Improvement';
    case 'breaking':
      return 'Breaking Change';
  }
};

/**
 * Get the badge color for a change category
 */
const getCategoryBadgeClass = (category: ChangelogEntry['changes'][0]['category']) => {
  switch (category) {
    case 'feature':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
    case 'bugfix':
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    case 'improvement':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300';
    case 'breaking':
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
  }
};

export const ChangelogModal: React.FC<ChangelogModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 dark:bg-black/70"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              What's New
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Latest updates and improvements to the Traveller Ship Builder
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close changelog"
          >
            <CloseIcon sx={{ fontSize: 24 }} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-8">
            {CHANGELOG.map((entry, index) => (
              <div key={entry.version} className="relative">
                {/* Version Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                      v{entry.version}
                    </span>
                    {index === 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                        Latest
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(entry.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  {entry.title}
                </h3>

                {/* Changes List */}
                <div className="space-y-3">
                  {entry.changes.map((change, changeIndex) => (
                    <div
                      key={changeIndex}
                      className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900/70 transition-colors"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getCategoryIcon(change.category)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getCategoryBadgeClass(change.category)}`}>
                            {getCategoryLabel(change.category)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {change.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Divider (except for last entry) */}
                {index < CHANGELOG.length - 1 && (
                  <div className="mt-8 border-t border-gray-200 dark:border-gray-700" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            For detailed documentation, visit the{' '}
            <a
              href="https://github.com/your-repo/traveller-ship-builder"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              GitHub repository
            </a>
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

